import React, { useMemo, useState } from 'react';

import { Button, IconButton } from '@src/components/Buttons';
import { IconEventWrapper, IconSwitcher } from '@src/components/Icon';
import InfoThickIcon from '@src/components/Icon/InfoThickIcon';
import { Tooltip } from '@src/components/Tooltip';
import { useNewThemeActive } from '@src/theming';

import InternalInput from './InternalInput';
import { PasswordInput } from './PasswordInput';
import {
  ButtonWrapper,
  ContentWrapper,
  IconWrapper,
  InfoText,
  InputContainer,
  InputWrapper,
  Label,
  RightIcons,
} from './styles';
import { defaultTheme, newTheme } from './theme';
import { TextInputProps } from './types';
import { getIconTheme } from './utils';

/**
 * @deprecated Use components from `ui-kit/core/Inputs`
 */
const TextInput: React.FC<TextInputProps> = ({
  disabled = false,
  activeOnHover = true,
  activeOnFocus = true,
  dataTestId = 'text-input',
  theme,
  ...props
}) => {
  const isNewThemeActive = useNewThemeActive();

  const _theme = useMemo(
    () => ({
      ...defaultTheme,
      ...(isNewThemeActive ? newTheme : {}),
      ...theme,
      rightIcon: { ...defaultTheme.rightIcon, ...theme?.rightIcon },
    }),
    [theme, isNewThemeActive]
  );
  const { height: rightIconHeight, ...rightIconTheme } = _theme.rightIcon;
  const iconTheme = getIconTheme(_theme, props.errorMessage);

  const [type, setType] = useState<'password' | 'text'>(
    props.isPasswordInput ? 'password' : 'text'
  );

  const error = useMemo(() => {
    return props.errorMessage;
  }, [props.errorMessage]);

  const inputName = props.name ?? props.label;

  return (
    <InputWrapper
      $width={_theme.width}
      data-testid={dataTestId}
      onClick={(e): void => e.stopPropagation()}
    >
      {props.label && (
        <Label
          $error={!!error}
          $theme={_theme}
          as="label"
          data-testid="text-input--label"
          htmlFor={inputName}
        >
          {props.label}
        </Label>
      )}
      <InputContainer
        $disabled={disabled}
        $error={!!props.errorMessage}
        $isActive={props.isActive}
        $theme={_theme}
      >
        <ContentWrapper>
          <IconEventWrapper
            activeOnFocus={activeOnFocus}
            activeOnHover={activeOnHover}
            disabled={disabled}
            fitParent={true}
            theme={iconTheme}
          >
            {props.icon && props.icon.position === 'start' && (
              <Tooltip text={props.icon.tooltipText}>
                <IconWrapper $padding={_theme.innerPadding} $position="left">
                  <IconSwitcher
                    disabled={disabled}
                    height={_theme.icon.height}
                    icon={props.icon.component}
                    isActive={props.isActive}
                    theme={iconTheme}
                  />
                </IconWrapper>
              </Tooltip>
            )}
            <InternalInput
              {...props}
              hasRightMargin={!!props.icon && props.icon.position === 'end'}
              inputName={inputName}
              theme={_theme}
              type={type}
            />
            {!props.errorMessage &&
              props.icon &&
              props.icon.position === 'end' && (
                <IconWrapper $padding={_theme.innerPadding} $position="right">
                  <IconSwitcher
                    disabled={disabled}
                    height={_theme.icon.height}
                    icon={props.icon.component}
                    theme={iconTheme}
                  />
                </IconWrapper>
              )}
            {!props.errorMessage && props.rightButton?.onClick && (
              <ButtonWrapper $padding={_theme.innerPadding}>
                <Button
                  disabled={props.rightButton.disabled}
                  height="auto"
                  label={props.rightButton.label}
                  name="textInputRightButton"
                  theme={{ fontSize: '9px' }}
                  width="50px"
                  onClick={props.rightButton.onClick}
                />
              </ButtonWrapper>
            )}
            {props.rightButton?.content && (
              <ButtonWrapper $padding={_theme.innerPadding}>
                {props.rightButton.content}
              </ButtonWrapper>
            )}
          </IconEventWrapper>
          <RightIcons>
            {props.rightIcon && (
              <>
                {props.rightIcon.onClick ? (
                  <IconButton
                    ariaLabel={props.rightIcon.label ?? ''}
                    height={rightIconHeight}
                    icon={props.rightIcon.component}
                    name="textInputRightIcon"
                    theme={rightIconTheme}
                    onClick={props.rightIcon.onClick}
                  />
                ) : (
                  <IconSwitcher
                    height={rightIconHeight}
                    icon={props.rightIcon.component}
                    theme={rightIconTheme}
                  />
                )}
              </>
            )}
            {props.isPasswordInput && (
              <PasswordInput
                error={error}
                theme={_theme}
                onTogglePasswordVisibility={(visible): void => {
                  setType(visible ? 'password' : 'text');
                  props.onTogglePasswordVisibility?.(visible);
                }}
              />
            )}
            {props.errorMessage && (
              <InfoThickIcon
                height="18px"
                theme={{
                  color: _theme.colorError,
                  secondaryColor: _theme.backgroundColor,
                }}
              />
            )}
          </RightIcons>
        </ContentWrapper>
      </InputContainer>
      {(props.infoText || error) && (
        <InfoText $error={!!error} $theme={_theme}>
          {error || props.infoText}
        </InfoText>
      )}
    </InputWrapper>
  );
};

export default TextInput;
